import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

// Alien view constants
const ENTER_DURATION = 1500;
const EXIT_DURATION = 1200;
const MORPH_DELAY = 800;

/**
 * Camera management: star follow, alien view arc animation.
 */
export class CameraController {
  // Alien view
  private alienViewActive = false;
  private alienAnimId: number | null = null;
  private alienMorphTimeout: ReturnType<typeof setTimeout> | null = null;
  private savedCamPos: THREE.Vector3 | null = null;
  private savedCamTarget: THREE.Vector3 | null = null;
  private savedAutoRotate = false;

  // Callbacks
  private onMorphToFace: (() => void) | null = null;
  private onMorphToText: (() => void) | null = null;

  constructor(
    private camera: THREE.PerspectiveCamera,
    private controls: OrbitControls,
    private renderer: THREE.WebGLRenderer
  ) {}

  setMorphCallbacks(onFace: () => void, onText: () => void): void {
    this.onMorphToFace = onFace;
    this.onMorphToText = onText;
  }

  /**
   * Fly the camera to look at an arbitrary world point (e.g. a star).
   * Approaches along the center→point ray so the point stays between the
   * camera and the galaxy core. Reuses the shared anim slot, so starting a
   * new flight (or returnToMainView / alien view) cancels this one.
   */
  flyToPoint(target: THREE.Vector3, distance: number = 26, duration: number = 2200): void {
    this.cancelAlienAnim();

    const startPos = this.camera.position.clone();
    const startTarget = this.controls.target.clone();

    const dir = target.clone();
    dir.y = 0;
    if (dir.lengthSq() < 1e-4) {
      dir.set(0, 0, 1);
    } else {
      dir.normalize();
    }

    const endPos = target.clone().add(dir.multiplyScalar(distance));
    endPos.y = target.y + distance * 0.45;
    const endTarget = target.clone();
    const startTime = performance.now();

    const animate = () => {
      const elapsed = performance.now() - startTime;
      const t = Math.min(elapsed / duration, 1);
      const eased = easeInOutCubic(t);

      this.camera.position.lerpVectors(startPos, endPos, eased);
      this.controls.target.lerpVectors(startTarget, endTarget, eased);
      this.controls.update();

      if (t < 1) {
        this.alienAnimId = requestAnimationFrame(animate);
      } else {
        this.alienAnimId = null;
      }
    };

    animate();
  }

  returnToMainView(duration: number = 2500): void {
    this.cancelAlienAnim();

    const startPos = this.camera.position.clone();
    const startTarget = this.controls.target.clone();
    const endPos = new THREE.Vector3(0, 60, 120);
    const endTarget = new THREE.Vector3(0, 0, 0);
    const startTime = performance.now();

    const startR = Math.sqrt(startPos.x * startPos.x + startPos.z * startPos.z);
    const endR = Math.sqrt(endPos.x * endPos.x + endPos.z * endPos.z);
    const startAngle = Math.atan2(startPos.x, startPos.z);
    const endAngle = Math.atan2(endPos.x, endPos.z);

    let angleDiff = endAngle - startAngle;
    if (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
    if (angleDiff < -Math.PI) angleDiff += Math.PI * 2;

    const animate = () => {
      const elapsed = performance.now() - startTime;
      const t = Math.min(elapsed / duration, 1);
      const eased = easeInOutCubic(t);

      const currentAngle = startAngle + angleDiff * eased;
      const currentR = startR + (endR - startR) * eased;

      this.camera.position.x = Math.sin(currentAngle) * currentR;
      this.camera.position.z = Math.cos(currentAngle) * currentR;
      this.camera.position.y = startPos.y + (endPos.y - startPos.y) * eased;

      this.controls.target.lerpVectors(startTarget, endTarget, eased);
      this.controls.update();

      if (t < 1) {
        this.alienAnimId = requestAnimationFrame(animate);
      } else {
        this.alienAnimId = null;
      }
    };

    animate();
  }

  // --- Alien view ---

  enterAlienView(): void {
    this.cancelAlienAnim();
    this.alienViewActive = true;

    this.savedCamPos = this.camera.position.clone();
    this.savedCamTarget = this.controls.target.clone();
    this.savedAutoRotate = this.controls.autoRotate;
    this.controls.autoRotate = false;

    if (this.onMorphToFace) {
      this.alienMorphTimeout = setTimeout(() => this.onMorphToFace!(), MORPH_DELAY);
    }

    const isMobile = window.innerWidth < 768;
    const targetY = isMobile ? 30 : 40;
    const cameraY = isMobile ? 60 : 80;
    const cameraZ = isMobile ? -280 : -320;

    const endPos = new THREE.Vector3(0, cameraY, cameraZ);
    const endTarget = new THREE.Vector3(0, targetY, 0);
    const startPos = this.camera.position.clone();
    const startTarget = this.controls.target.clone();
    const startTime = performance.now();

    const startR = Math.sqrt(startPos.x * startPos.x + startPos.z * startPos.z);
    const endR = Math.sqrt(endPos.x * endPos.x + endPos.z * endPos.z);
    const startAngle = Math.atan2(startPos.x, startPos.z);
    const endAngle = Math.atan2(endPos.x, endPos.z);

    let angleDiff = endAngle - startAngle;
    if (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
    if (angleDiff < -Math.PI) angleDiff += Math.PI * 2;

    const animateToAlienView = () => {
      const elapsed = performance.now() - startTime;
      const t = Math.min(elapsed / ENTER_DURATION, 1);
      const eased = easeInOutCubic(t);

      const currentAngle = startAngle + angleDiff * eased;
      const currentR = startR + (endR - startR) * eased;

      this.camera.position.x = Math.sin(currentAngle) * currentR;
      this.camera.position.z = Math.cos(currentAngle) * currentR;
      this.camera.position.y = startPos.y + (endPos.y - startPos.y) * eased;

      this.controls.target.lerpVectors(startTarget, endTarget, eased);

      if (t < 1) {
        this.alienAnimId = requestAnimationFrame(animateToAlienView);
      } else {
        this.alienAnimId = null;
      }
    };

    animateToAlienView();
  }

  exitAlienView(): void {
    this.cancelAlienAnim();
    this.alienViewActive = false;

    this.onMorphToText?.();

    const startPos = this.camera.position.clone();
    const startTarget = this.controls.target.clone();
    const endPos = this.savedCamPos || new THREE.Vector3(0, 60, 120);
    const endTarget = this.savedCamTarget || new THREE.Vector3(0, 0, 0);
    const startTime = performance.now();

    const startR = Math.sqrt(startPos.x * startPos.x + startPos.z * startPos.z);
    const endR = Math.sqrt(endPos.x * endPos.x + endPos.z * endPos.z);
    const startAngle = Math.atan2(startPos.x, startPos.z);
    const endAngle = Math.atan2(endPos.x, endPos.z);

    let angleDiff = endAngle - startAngle;
    if (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
    if (angleDiff < -Math.PI) angleDiff += Math.PI * 2;

    const animateBack = () => {
      const elapsed = performance.now() - startTime;
      const t = Math.min(elapsed / EXIT_DURATION, 1);
      const eased = easeInOutCubic(t);

      const currentAngle = startAngle + angleDiff * eased;
      const currentR = startR + (endR - startR) * eased;

      this.camera.position.x = Math.sin(currentAngle) * currentR;
      this.camera.position.z = Math.cos(currentAngle) * currentR;
      this.camera.position.y = startPos.y + (endPos.y - startPos.y) * eased;

      this.controls.target.lerpVectors(startTarget, endTarget, eased);

      if (t < 1) {
        this.alienAnimId = requestAnimationFrame(animateBack);
      } else {
        this.alienAnimId = null;
        this.controls.autoRotate = this.savedAutoRotate;
      }
    };

    animateBack();
  }

  private cancelAlienAnim(): void {
    if (this.alienAnimId !== null) {
      cancelAnimationFrame(this.alienAnimId);
      this.alienAnimId = null;
    }
    if (this.alienMorphTimeout !== null) {
      clearTimeout(this.alienMorphTimeout);
      this.alienMorphTimeout = null;
    }
  }

  destroy(): void {
    this.cancelAlienAnim();
  }
}
